/**
 * Small copies of the pictures on cards, for cards seen from far away (utils/farZoom.ts). A
 * full-size picture costs the browser about 33 ms to decode (2048 px PNG) every time it is
 * drawn at a new size, and a zoomed-out canvas can show a hundred or more of them; a copy 320 px
 * wide costs next to nothing.
 *
 * A copy is made once, three at a time, off the main thread (decoded and scaled down by
 * createImageBitmap): from this session's bytes or the app's own server (the desktop app's
 * local folder) when it is shown from there, otherwise, for a picture known by a file
 * reference, from its stored copy through the media loader (the local folder first, else one
 * download, which the browser's cache usually answers). On the website the pictures are on
 * another domain, so before this they were drawn far away at full size: every zoom-out decoded
 * them all again on the threads that draw the page, which left parts of the canvas white for
 * seconds. A picture known by a file reference keeps its copy in this browser (the media
 * store's thumbnails), so the next session has it at once; any other picture's copy lasts for
 * the session.
 */
import { getEditorMedia, putEditorMedia } from './editorMediaStore';
import { extractFileId, isFileReference } from './fileRegistry';
import { loadMediaBlob, type LoadMediaOptions } from './mediaLoader';

export const SMALL_PICTURE_WIDTH = 320;
const QUALITY = 0.82;
/** How many copies are made at the same time; the rest wait their turn (98 pictures took 5 s two at a time, the first time) */
const PARALLEL = 3;

/** Whether a copy can be made from this address without downloading the picture */
export const canShrinkFrom = (url: string, pageOrigin: string): boolean => {
    if (/^(blob|data):/i.test(url)) return true;
    try {
        return new URL(url).origin === pageOrigin;
    } catch {
        return false;
    }
};

const canShrink = () => typeof createImageBitmap === 'function' && typeof OffscreenCanvas !== 'undefined';

/** The picture at `url`, decoded and scaled down to `width` off the main thread; null where the browser cannot */
export const shrinkPicture = async (url: string, width: number = SMALL_PICTURE_WIDTH): Promise<Blob | null> => {
    if (!canShrink()) return null;
    const response = await fetch(url);
    if (!response.ok) return null;
    return shrinkBlob(await response.blob(), width);
};

/** A picture's bytes, decoded and scaled down to `width` off the main thread; null where the browser cannot */
export const shrinkBlob = async (source: Blob, width: number = SMALL_PICTURE_WIDTH): Promise<Blob | null> => {
    if (!canShrink()) return null;
    // Its width decides; its height follows the picture's own shape
    const bitmap = await createImageBitmap(source, { resizeWidth: width, resizeQuality: 'medium' });
    try {
        const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
        const context = canvas.getContext('2d');
        if (!context) return null;
        context.drawImage(bitmap, 0, 0);
        return await canvas.convertToBlob({ type: 'image/webp', quality: QUALITY });
    } finally {
        bitmap.close();
    }
};

let running = 0;
const waiting: Array<() => void> = [];
const inTurn = <T,>(work: () => Promise<T>): Promise<T> => new Promise<T>((resolve, reject) => {
    const go = () => {
        running++;
        work().then(resolve, reject).finally(() => {
            running--;
            waiting.shift()?.();
        });
    };
    if (running < PARALLEL) go();
    else waiting.push(go);
});

const made = new Map<string, Promise<string | null>>();

/** Where a picture known by a file reference keeps its copy; other pictures' copies are not kept */
const keptKey = (ref: string): string | null => {
    const id = isFileReference(ref) ? extractFileId(ref) : null;
    return id ? `thumb:far-${id}@${SMALL_PICTURE_WIDTH}` : null;
};

/**
 * A small copy of the picture `ref`, shown from `url`, as an address to show it from. When
 * `url` is on another domain (the cloud, on the website), a file reference's copy is made from
 * the bytes the media loader gets with `load` (whose workspace and owner). Null when no copy can
 * be made here: show `url` itself.
 */
export const smallPicture = (ref: string, url: string, load?: LoadMediaOptions): Promise<string | null> => {
    const key = isFileReference(ref) ? ref : url;
    const known = made.get(key);
    if (known) return known;
    const pending = (async () => {
        const storeKey = keptKey(ref);
        const kept = storeKey ? await getEditorMedia(storeKey).catch(() => null) : null;
        if (kept) return URL.createObjectURL(kept);
        if (typeof location === 'undefined' || !canShrink()) return null;
        const here = canShrinkFrom(url, location.origin);
        if (!here && !(load && isFileReference(ref))) return null;
        const copy = await inTurn(async () => (here ? shrinkPicture(url) : shrinkBlob((await loadMediaBlob(ref, { ...load, label: 'the picture' })).blob)));
        if (!copy) return null;
        if (storeKey) putEditorMedia(storeKey, copy).catch(() => undefined);
        return URL.createObjectURL(copy);
    })().catch(() => null);
    made.set(key, pending);
    // A failure is tried again next time, when the address may work
    pending.then(result => { if (result === null && made.get(key) === pending) made.delete(key); });
    return pending;
};

/**
 * The small copy of the picture `ref` this browser keeps (or this session made), without
 * looking the picture itself up: on the website that lookup is a read of the file registry per
 * picture. Null when there is none yet.
 */
export const keptSmallPicture = async (ref: string): Promise<string | null> => {
    const storeKey = keptKey(ref);
    if (!storeKey) return null;
    const known = made.get(ref);
    if (known) return known;
    const kept = await getEditorMedia(storeKey).catch(() => null);
    if (!kept) return null;
    // Made meanwhile (another card with the same picture): one address for both
    const meanwhile = made.get(ref);
    if (meanwhile) return meanwhile;
    const shown = Promise.resolve<string | null>(URL.createObjectURL(kept));
    made.set(ref, shown);
    return shown;
};

/** For tests: forget every copy made in this session */
export const forgetSmallPictures = (): void => {
    made.clear();
};
